<template>
  <v-container class="automatic-kpi-form">
    <!-- Header with action buttons -->
    <div class="action-buttons">
      <v-btn color="primary" @click="openSelectionModal">
        <v-icon left>mdi-plus-circle</v-icon>
        Add KPIs
      </v-btn>
    </div>

    <!-- Selected KPIs List -->
    <div v-if="selectedKpiList.length === 0" class="empty-state">
      <v-icon size="80" color="primary">mdi-chart-line</v-icon>
      <h3>No KPIs added to the problem</h3>
      <p>Add or select KPIs</p>
    </div>

    <div v-else class="kpis-container">
      <div class="list">
        <kpi-card
          v-for="kpi in selectedKpiList"
          :key="kpi.id"
          :kpi="kpi"
          @delete="removeFromSelectedList"
          @edit="openEditModal"
        />
      </div>
    </div>

    <!-- KPI Selection Modal -->
    <kpi-selection-modal
      v-model="showSelectionModal"
      :kpis="allKpis"
      :selected-kpi-ids="selectedKpiIds"
      @select="addKpiToList"
      @create-new="createNewManually"
      @toggle="toggleKpiSelection"
      @close="showSelectionModal = false"
    />

    <!-- KPI Edit Modal -->
    <kpi-edit-modal
      :value="showEditModal"
      :kpi="kpiToEdit"
      :saving="isSaving"
      @close="showEditModal = $event"
      @save="saveKpi"
      @delete="confirmDeleteKpi"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="450">
      <v-card class="confirm-dialog">
        <v-card-title class="confirm-dialog__title">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Confirm Deletion
        </v-card-title>
        <v-card-text class="confirm-dialog__content">
          <p>Are you sure you want to delete this KPI?</p>
          <p v-if="kpiToDelete" class="kpi-name">
            {{ kpiToDelete.name }}
          </p>
          <p class="warning-text">This action cannot be undone.</p>
        </v-card-text>
        <v-card-actions class="confirm-dialog__actions">
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteKpi">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      :color="snackbarColor"
      @click="snackbar = false"
      style="cursor: pointer"
    >
      <v-icon left v-if="snackbarColor === 'success'">mdi-check-circle</v-icon>
      <v-icon left v-else-if="snackbarColor === 'error'"
        >mdi-alert-circle</v-icon
      >
      <v-icon left v-else>mdi-information</v-icon>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import KpiCard from "./KpiCard.vue";
import KpiSelectionModal from "./KpiSelectionModal.vue";
import KpiEditModal from "./KpiEditModal.vue";
import apiClient from "@/plugins/apiService";

export default {
  name: "AutomaticKpiForm",

  components: {
    KpiCard,
    KpiSelectionModal,
    KpiEditModal,
  },

  props: {
    selectedLab: {
      type: [String, Number],
      default: null,
    },
    selectedProblem: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      // KPIs data
      allKpis: [],
      selectedKpiList: [],
      selectedKpiId: null,

      // Modal states
      showSelectionModal: false,
      showEditModal: false,
      kpiToEdit: null,

      // Delete confirmation
      confirmDialog: false,
      kpiToDelete: null,

      // Loading states
      isSaving: false,

      // Snackbar
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
    };
  },

  computed: {
    selectedKpiIds() {
      return this.selectedKpiList.map((k) => k.id);
    },
  },

  watch: {
    selectedProblem() {
      this.fetchKpis();
    },
    selectedLab() {
      this.fetchKpis();
    },
  },

  mounted() {
    this.fetchKpis();
  },

  methods: {
    // ==================== Modal Controls ====================
    openSelectionModal() {
      this.showSelectionModal = true;
    },

    openEditModal(kpi) {
      this.kpiToEdit = { ...kpi };
      this.showEditModal = true;
    },

    // ==================== Selection Methods ====================
    async addKpiToList(kpi) {
      if (kpi?.error) {
        this.showErrorMessage(kpi.error);
        throw new Error(kpi.error);
      }

      if (this.selectedKpiIds.includes(kpi.id)) {
        return;
      }

      this.selectedKpiList.push(kpi);

      try {
        const response = await apiClient.put(`/add-kpi/${kpi.id}`, {
          selectedProblem: this.selectedProblem,
        });

        if (response.status !== 200) {
          throw new Error("Failed to add KPI");
        }

        this.showSuccessMessage("KPI added to selection");
      } catch (error) {
        console.error("Error adding KPI to list:", error);
        // Remove from local list if API call failed
        const index = this.selectedKpiList.findIndex((k) => k.id === kpi.id);
        if (index !== -1) {
          this.selectedKpiList.splice(index, 1);
        }
        this.showErrorMessage("Failed to add KPI");
        throw error;
      }
    },

    async removeFromSelectedList(kpiId) {
      const index = this.selectedKpiList.findIndex((item) => item.id === kpiId);

      if (index === -1) return;

      const removedKpi = this.selectedKpiList[index];
      this.selectedKpiList.splice(index, 1);

      try {
        const response = await apiClient.delete(`/delete-kpi/${kpiId}`);

        if (response.status !== 200) {
          throw new Error("Failed to remove KPI");
        }

        this.showSuccessMessage("KPI removed");
      } catch (error) {
        console.error("Error removing KPI:", error);
        // Restore the KPI to the list if API call failed
        this.selectedKpiList.splice(index, 0, removedKpi);
        this.showErrorMessage("Failed to remove KPI");
      }
    },

    async toggleKpiSelection(kpi) {
      if (this.selectedKpiIds.includes(kpi.id)) {
        await this.removeFromSelectedList(kpi.id);
      } else {
        await this.addKpiToList(kpi);
      }
    },

    // ==================== Creation Methods ====================
    createNewManually() {
      const newTempKpi = {
        id: "temp-" + new Date().getTime(),
        name: "New KPI",
        unit: "",
        pillar: "",
        impact: "",
        description: "",
        baseLineDataNeeded: false,
        responsibilityDefinition: "",
        responsibilityCalculation: "",
        dataRequirements: "",
        formula: "",
        temp: true,
      };

      this.kpiToEdit = newTempKpi;
      this.showSelectionModal = false;
      this.showEditModal = true;
    },

    // ==================== API Methods ====================
    async fetchKpis() {
      if (!this.selectedProblem) return;

      try {
        const response = await apiClient.get(`/kpi/${this.selectedProblem}`);
        const data = response.data;

        // All available KPIs
        this.allKpis = data;

        // Currently selected KPIs
        this.selectedKpiList = data.filter((k) => k.selectedKpi);
      } catch (error) {
        console.error("Error fetching KPIs:", error);
        this.allKpis = [];
        this.selectedKpiList = [];
        this.showErrorMessage("Failed to load KPIs");
      }
    },

    async saveKpi(kpiData) {
      this.isSaving = true;

      try {
        const isUpdate =
          kpiData.id && !kpiData.id.toString().startsWith("temp-");
        const url = isUpdate ? `/kpi/${kpiData.id}` : `/kpi`;

        const payload = {
          ...kpiData,
          selectedProblem: this.selectedProblem,
          selectedLab: this.selectedLab,
        };

        const response = isUpdate
          ? await apiClient.put(url, payload)
          : await apiClient.post(url, payload);

        if (response.status === 200 || response.status === 201) {
          const savedKpi = response.data;

          if (!isUpdate) {
            // For new KPIs, update the temp ID
            kpiData.id = savedKpi.id;
            delete kpiData.temp;
          }

          // Update or add to the lists
          const index = this.selectedKpiList.findIndex(
            (k) => k.id === (isUpdate ? kpiData.id : savedKpi.id)
          );

          if (index !== -1) {
            this.selectedKpiList.splice(index, 1, savedKpi);
          } else {
            this.selectedKpiList.push(savedKpi);
          }

          this.showSelectionModal = false;
          this.showEditModal = false;
          this.showSuccessMessage(isUpdate ? "KPI updated" : "KPI created");

          await this.fetchKpis();
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.error("Error saving KPI:", error);
        this.showErrorMessage("Failed to save KPI");
      } finally {
        this.isSaving = false;
      }
    },

    // ==================== Delete Operations ====================
    confirmDeleteKpi(kpi) {
      this.kpiToDelete = kpi;
      this.confirmDialog = true;
      this.showEditModal = false;
    },

    async deleteKpi() {
      if (!this.kpiToDelete) return;

      try {
        const response = await apiClient.delete(`/kpi/${this.kpiToDelete.id}`);

        if (response.status === 200) {
          const index = this.selectedKpiList.findIndex(
            (k) => k.id === this.kpiToDelete.id
          );
          if (index !== -1) {
            this.selectedKpiList.splice(index, 1);
          }

          this.confirmDialog = false;
          this.showSuccessMessage("KPI deleted successfully");
          await this.fetchKpis();
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.error("Error deleting KPI:", error);
        this.showErrorMessage("Failed to delete KPI");
      } finally {
        this.kpiToDelete = null;
      }
    },

    // ==================== Notifications ====================
    showSuccessMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = "success";
      this.snackbar = true;
    },

    showErrorMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = "error";
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

/* KPI List */
.kpis-container {
  display: flex;
  flex-direction: column;
  max-height: 700px;
  overflow-y: auto;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Confirm Dialog */
.confirm-dialog {
  background-color: var(--secondaryColor) !important;
}

.confirm-dialog__title {
  color: var(--backgroundColor) !important;
}

.confirm-dialog__content {
  padding: 16px 24px;
}

.confirm-dialog__content p {
  margin-bottom: 8px;
  color: var(--backgroundColor);
}

.confirm-dialog__content .kpi-name {
  padding: 12px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  text-align: center;
}

.confirm-dialog__content .warning-text {
  color: var(--error);
  font-size: 0.875rem;
  opacity: 0.8;
}

.confirm-dialog__actions {
  padding: 12px 16px;
}
</style>
